/**
 * FaqAgentChat - World-class customer support chat widget for QikTruck.
 *
 * Features:
 * - Mode switching (FAQ / Quote / Support)
 * - AI-powered responses with OpenAI + Anthropic fallback
 * - Rich media: images, audio/voicenotes, PDFs, video
 * - Quote calculation with interactive quote cards
 * - Admin takeover detection
 * - Polling-based real-time updates (5s)
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import cookie from "react-cookies";
import Spinner from "../common/Spinner";
import toast from "../common/Toast";
import {
  MessageOutlined,
  CloseOutlined,
  SendOutlined,
  PaperClipOutlined,
  QuestionCircleOutlined,
  CalculatorOutlined,
  CustomerServiceOutlined,
  FileTextOutlined,
  PlayCircleOutlined,
  SoundOutlined,
  FilePdfOutlined,
  CloseCircleFilled,
  RobotOutlined,
  UserOutlined,
  TeamOutlined,
} from "../common/Icons";
import constants from "../../Constants";
import "./FaqAgentChat.scss";

const baseURL = constants.baseURL;

const MODES = [
  { key: "faq", label: "Ask a question", icon: <QuestionCircleOutlined /> },
  { key: "quote", label: "Get a quote", icon: <CalculatorOutlined /> },
];

const IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];
const AUDIO_TYPES = ["audio/mpeg", "audio/ogg", "audio/wav", "audio/webm", "audio/mp4", "audio/aac"];
const VIDEO_TYPES = ["video/mp4", "video/webm", "video/quicktime"];
const ALLOWED_TYPES = [
  ...IMAGE_TYPES, ...AUDIO_TYPES, ...VIDEO_TYPES,
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const POLL_INTERVAL = 5000;

// Simple markdown renderer
function renderMarkdown(text) {
  if (!text) return null;
  return text.split("\n").map((line, i) => {
    const trimmed = line.trim();
    if (!trimmed) return <br key={i} />;

    // Bold
    let html = trimmed
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>")
      .replace(/`(.+?)`/g, "<code>$1</code>");

    // Bullet
    if (/^[-•*]\s/.test(trimmed)) {
      html = html.replace(/^[-•*]\s/, "");
      return <li key={i} dangerouslySetInnerHTML={{ __html: html }} />;
    }
    if (/^\d+[.)]\s/.test(trimmed)) {
      html = html.replace(/^\d+[.)]\s/, "");
      return <li key={i} dangerouslySetInnerHTML={{ __html: html }} />;
    }

    return <p key={i} dangerouslySetInnerHTML={{ __html: html }} />;
  });
}

function formatTime(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" });
  return isToday ? time : `${d.toLocaleDateString("en-ZA", { day: "numeric", month: "short" })} ${time}`;
}

const FaqAgentChat = () => {
  // TODO: activate when ready
  const ENABLED = false;

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("faq-chat-session") || "");
  const [mode, setMode] = useState("faq");
  const [status, setStatus] = useState("active");
  const [started, setStarted] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [fabPulse, setFabPulse] = useState(true);
  const [unread, setUnread] = useState(0);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const pollRef = useRef(null);
  const lastMsgCountRef = useRef(0);

  const getAuth = useCallback(() => {
    const token = cookie.load("quotetoken") || cookie.load("qtcid_");
    return token ? { headers: { Authorization: token } } : {};
  }, []);

  // Check if user is scrolled near the bottom (within 150px)
  const isNearBottom = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 150;
  }, []);

  // Scroll to bottom only if user is already near the bottom
  const scrollToBottom = useCallback((force = false) => {
    if (!force && !isNearBottom()) return;
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  }, [isNearBottom]);

  // ── Fetch messages (polling) ────────────────────────────────────
  const fetchMessages = useCallback(async () => {
    if (!sessionId) return;
    try {
      const res = await axios.get(`${baseURL}/customer-faq-chat?sessionId=${sessionId}`, getAuth());
      const data = res.data;
      if (data.messages?.length > 0) {
        setMessages(data.messages);
        if (data.session) {
          setMode(data.session.mode || "faq");
          setStatus(data.session.status || "active");
        }
        // Unread detection
        if (!open && data.messages.length > lastMsgCountRef.current) {
          setUnread((prev) => prev + (data.messages.length - lastMsgCountRef.current));
          // Play notification
          try {
            const audio = new Audio("/notification1.mp3");
            audio.play().catch(() => {});
          } catch (_) {}
        }
        lastMsgCountRef.current = data.messages.length;
        if (open) scrollToBottom();
      }
    } catch (err) {
      // silent - don't spam errors on poll
    }
  }, [sessionId, open, getAuth, scrollToBottom]);

  // Start polling when open + session exists
  useEffect(() => {
    if (sessionId && started) {
      fetchMessages();
      pollRef.current = setInterval(fetchMessages, POLL_INTERVAL);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [sessionId, started, fetchMessages]);

  // Clear unread when opening
  useEffect(() => {
    if (open) { setUnread(0); setFabPulse(false); }
  }, [open]);

  // Auto-resize textarea
  const handleInputChange = (e) => {
    setInput(e.target.value);
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = "auto";
      ta.style.height = Math.min(ta.scrollHeight, 100) + "px";
    }
  };

  // ── Upload files to S3 ─────────────────────────────────────────
  const uploadFile = async (file) => {
    try {
      // Get signed URL
      const urlRes = await axios.post(`${baseURL}/customer-faq-chat-upload-url`, {
        fileName: file.name,
        fileType: file.type,
        sessionId,
      }, getAuth());

      const { uploadUrl, viewUrl, key } = urlRes.data;

      // Upload to S3
      await axios.put(uploadUrl, file, {
        headers: { "Content-Type": file.type },
        onUploadProgress: (e) => {
          const pct = Math.round((e.loaded / e.total) * 100);
          setPendingFiles((prev) =>
            prev.map((f) => f.id === file._id ? { ...f, progress: pct } : f)
          );
        },
      });

      return { key, type: file.type, name: file.name, size: file.size, viewUrl };
    } catch (err) {
      console.error("Upload error:", err);
      toast.error(`Failed to upload ${file.name}`);
      return null;
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const valid = files.filter((f) => {
      if (!ALLOWED_TYPES.includes(f.type)) {
        toast.warning(`${f.name}: unsupported file type`);
        return false;
      }
      if (f.size > MAX_FILE_SIZE) {
        toast.warning(`${f.name}: file too large (max 10MB)`);
        return false;
      }
      return true;
    });

    const previews = valid.map((f) => {
      f._id = Date.now() + Math.random();
      return {
        id: f._id,
        file: f,
        name: f.name,
        type: f.type,
        preview: IMAGE_TYPES.includes(f.type) ? URL.createObjectURL(f) : null,
        progress: 0,
      };
    });

    setPendingFiles((prev) => [...prev, ...previews]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removePendingFile = (id) => {
    setPendingFiles((prev) => prev.filter((f) => f.id !== id));
  };

  // ── Send message ───────────────────────────────────────────────
  const handleSend = async (overrideText) => {
    const text = (overrideText !== undefined ? overrideText : input).trim();
    if (!text && pendingFiles.length === 0) return;
    setSending(true);

    try {
      // Upload pending files first
      let uploadedAttachments = [];
      if (pendingFiles.length > 0) {
        setUploading(true);
        const results = await Promise.all(pendingFiles.map((pf) => uploadFile(pf.file)));
        uploadedAttachments = results.filter(Boolean);
        setUploading(false);
        setPendingFiles([]);
      }

      // Add optimistic customer message
      const optimisticMsg = {
        id: `temp_${Date.now()}`,
        role: "customer",
        content: text || `[Sent ${uploadedAttachments.length} file(s)]`,
        attachments: uploadedAttachments,
        timestamp: new Date().toISOString(),
        mode,
      };
      setMessages((prev) => [...prev, optimisticMsg]);
      setInput("");
      if (textareaRef.current) textareaRef.current.style.height = "auto";
      scrollToBottom(true);

      // Send to API
      const payload = {
        message: text,
        sessionId: sessionId || undefined,
        attachments: uploadedAttachments,
        mode,
        email: cookie.load("qtcid_email") || undefined,
        name: cookie.load("qtcid_name") || undefined,
        mobile: cookie.load("qtcid_mobile") || undefined,
      };

      const res = await axios.post(`${baseURL}/customer-faq-chat`, payload, getAuth());
      const data = res.data;

      // Store session
      if (data.sessionId) {
        setSessionId(data.sessionId);
        localStorage.setItem("faq-chat-session", data.sessionId);
      }
      if (data.mode) setMode(data.mode);
      if (data.status) setStatus(data.status);

      // Add AI response
      if (data.aiMessage) {
        const botMsg = {
          id: `bot_${Date.now()}`,
          role: "bot",
          content: data.aiMessage,
          timestamp: new Date().toISOString(),
          mode: data.mode || mode,
          metadata: {
            quoteResult: data.quoteResult,
            suggestedMode: data.suggestedMode,
          },
        };
        setMessages((prev) => [...prev, botMsg]);
        scrollToBottom(true);
      }

      // If taken over, show notice
      if (data.status === "taken_over" && data.message) {
        toast.info(data.message);
      }
    } catch (err) {
      console.error("Send error:", err);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── Mode switch ────────────────────────────────────────────────
  const handleModeSwitch = async (newMode) => {
    setMode(newMode);
    if (sessionId) {
      try {
        await axios.post(`${baseURL}/customer-faq-chat-mode`, { sessionId, mode: newMode }, getAuth());
      } catch (_) {}
    }
  };

  // ── Start chat ─────────────────────────────────────────────────
  const startChat = (selectedMode) => {
    setMode(selectedMode);
    setStarted(true);
    setMessages([]);
    const greeting = selectedMode === "quote"
      ? "I'd like to get a quote for a delivery."
      : "Hi, I have a question.";
    // Pass greeting directly to avoid stale closure
    handleSend(greeting);
  };

  // ── New session ────────────────────────────────────────────────
  const startNewSession = () => {
    setSessionId("");
    setMessages([]);
    setStarted(false);
    setStatus("active");
    setMode("faq");
    localStorage.removeItem("faq-chat-session");
  };

  // ── Render attachment ──────────────────────────────────────────
  const renderAttachment = (att, idx) => {
    if (!att) return null;

    if (IMAGE_TYPES.includes(att.type)) {
      return (
        <div key={idx} className="faq-attachment faq-attachment--image">
          <img src={att.viewUrl || att.url} alt={att.name || "image"} loading="lazy" />
        </div>
      );
    }
    if (AUDIO_TYPES.includes(att.type)) {
      return (
        <div key={idx} className="faq-attachment faq-attachment--audio">
          <audio controls preload="metadata">
            <source src={att.viewUrl || att.url} type={att.type} />
          </audio>
          {att.processed?.transcript && (
            <div style={{ fontSize: 11, color: "#636e72", marginTop: 4, fontStyle: "italic" }}>
              "{att.processed.transcript.substring(0, 100)}..."
            </div>
          )}
        </div>
      );
    }
    if (VIDEO_TYPES.includes(att.type)) {
      return (
        <div key={idx} className="faq-attachment faq-attachment--file">
          <PlayCircleOutlined />
          <a href={att.viewUrl || att.url} target="_blank" rel="noopener noreferrer">{att.name || "Video"}</a>
        </div>
      );
    }

    // Generic file
    const icon = att.type?.includes("pdf") ? <FilePdfOutlined /> : <FileTextOutlined />;
    return (
      <div key={idx} className="faq-attachment faq-attachment--file">
        {icon}
        <span>{att.name || "Document"}</span>
      </div>
    );
  };

  // ── Render quote card ──────────────────────────────────────────
  const renderQuoteCard = (quoteResult) => {
    if (!quoteResult?.quotes?.length) return null;
    return (
      <div className="faq-quote-card">
        <div className="faq-quote-header">
          <h5><CalculatorOutlined /> Delivery Quotes</h5>
          <span className="faq-quote-distance">{quoteResult.distanceKm} km</span>
        </div>
        <div className="faq-quote-route">
          <div className="route-line">
            <span className="route-dot pickup" />
            <span>{quoteResult.collection?.address || "Pickup"}</span>
          </div>
          <div className="route-line">
            <span className="route-dot delivery" />
            <span>{quoteResult.delivery?.address || "Delivery"}</span>
          </div>
        </div>
        <div className="faq-quote-options">
          {quoteResult.quotes.slice(0, 4).map((q, i) => (
            <div key={i} className="faq-quote-option">
              <div>
                <div className="faq-quote-vehicle">{q.vehicleType}</div>
                <div className="faq-quote-vehicle-desc">{q.description}</div>
              </div>
              <div className="faq-quote-price">R{q.total}</div>
            </div>
          ))}
        </div>
        <div className="faq-quote-cta">
          <button onClick={() => {
            const url = `/truck-hire?collection=${encodeURIComponent(quoteResult.collection?.address || "")}&delivery=${encodeURIComponent(quoteResult.delivery?.address || "")}`;
            window.location.href = url;
          }}>
            Book Now
          </button>
        </div>
      </div>
    );
  };

  // ── Render message ─────────────────────────────────────────────
  const renderMessage = (msg, idx) => {
    const isCustomer = msg.role === "customer";
    const isAdmin = msg.role === "admin";
    const roleClass = isCustomer ? "faq-msg--customer" : isAdmin ? "faq-msg--admin" : "faq-msg--bot";
    const avatarClass = isCustomer ? "avatar-customer" : isAdmin ? "avatar-admin" : "avatar-bot";
    const avatarIcon = isCustomer ? <UserOutlined /> : isAdmin ? <TeamOutlined /> : <RobotOutlined />;

    return (
      <div key={msg.id || idx} className={`faq-msg ${roleClass}`}>
        <div className={`faq-msg-avatar ${avatarClass}`}>{avatarIcon}</div>
        <div>
          {isAdmin && <div className="faq-msg-label">Support Agent</div>}
          <div className="faq-msg-bubble">
            {renderMarkdown(msg.content)}
            {msg.attachments?.length > 0 && (
              <div className="faq-attachments">
                {msg.attachments.map((att, i) => renderAttachment(att, i))}
              </div>
            )}
            {msg.metadata?.quoteResult && renderQuoteCard(msg.metadata.quoteResult)}
          </div>
          <div className="faq-msg-time">{formatTime(msg.timestamp)}</div>
          {msg.metadata?.suggestedMode && (
            <div className="faq-quick-actions">
              <button
                className="faq-quick-btn"
                onClick={() => handleModeSwitch(msg.metadata.suggestedMode)}
              >
                {msg.metadata.suggestedMode === "quote" ? "🚚 Switch to Quote Mode" : "Switch Mode"}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── Welcome screen ─────────────────────────────────────────────
  const renderWelcome = () => (
    <div className="faq-welcome">
      <div className="faq-welcome-icon">
        <MessageOutlined />
      </div>
      <h3>Hi there! 👋</h3>
      <p>I'm the QikTruck assistant. I can answer questions, help you get a quote, or connect you with our support team.</p>
      <div className="faq-welcome-actions">
        <button className="faq-welcome-btn" onClick={() => startChat("faq")}>
          <div className="faq-welcome-btn-icon icon-faq"><QuestionCircleOutlined /></div>
          <div className="faq-welcome-btn-text">
            <h5>Ask a Question</h5>
            <p>Get instant answers</p>
          </div>
        </button>
        <button className="faq-welcome-btn" onClick={() => startChat("quote")}>
          <div className="faq-welcome-btn-icon icon-quote"><CalculatorOutlined /></div>
          <div className="faq-welcome-btn-text">
            <h5>Get a Quote</h5>
            <p>Instant delivery pricing</p>
          </div>
        </button>
        <button className="faq-welcome-btn" onClick={() => { setStarted(true); setMode("faq"); }}>
          <div className="faq-welcome-btn-icon icon-support"><CustomerServiceOutlined /></div>
          <div className="faq-welcome-btn-text">
            <h5>Talk to Support</h5>
            <p>Connect with an agent</p>
          </div>
        </button>
      </div>
    </div>
  );

  // ══════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════
  if (!ENABLED) return null;

  return (
    <div className="faq-chat-wrapper">
      {/* ── Chat Panel ─────────────────────────────────────────── */}
      {open && (
        <div className="faq-chat-panel">
          {/* Header */}
          <div className="faq-chat-header">
            <div className="faq-chat-header-avatar">
              <RobotOutlined />
            </div>
            <div className="faq-chat-header-info">
              <h4>QikTruck Assistant</h4>
              <div className="faq-chat-header-status">
                <span className="status-dot" />
                {status === "taken_over" ? "Connected with agent" : "Online"}
              </div>
            </div>
            <div className="faq-chat-header-actions">
              {started && (
                <button onClick={startNewSession} title="New conversation">
                  <MessageOutlined />
                </button>
              )}
              <button onClick={() => setOpen(false)} title="Close">
                <CloseOutlined />
              </button>
            </div>
          </div>

          {/* Mode Switcher */}
          {started && status !== "taken_over" && (
            <div className="faq-chat-modes">
              {MODES.map((m) => (
                <button
                  key={m.key}
                  className={`faq-chat-mode-btn ${m.key === mode ? "active" : ""} mode-${m.key}`}
                  onClick={() => handleModeSwitch(m.key)}
                >
                  {m.icon} {m.label}
                </button>
              ))}
            </div>
          )}

          {/* Messages / Welcome */}
          {!started ? (
            renderWelcome()
          ) : (
            <>
              <div className="faq-chat-messages" ref={messagesContainerRef}>
                {messages.map((msg, idx) => renderMessage(msg, idx))}
                {sending && (
                  <div className="faq-typing">
                    <div className="faq-typing-dots">
                      <span /><span /><span />
                    </div>
                    <span className="faq-typing-text">
                      {status === "taken_over" ? "Agent is typing..." : "QikTruck AI is thinking..."}
                    </span>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Upload Previews */}
              {pendingFiles.length > 0 && (
                <div className="faq-upload-preview">
                  {pendingFiles.map((pf) => (
                    <div key={pf.id} className="faq-upload-item">
                      {pf.preview ? (
                        <img src={pf.preview} alt={pf.name} />
                      ) : (
                        <div className="faq-upload-file-icon">
                          {pf.type?.includes("audio") ? <SoundOutlined /> :
                           pf.type?.includes("pdf") ? <FilePdfOutlined /> :
                           <FileTextOutlined />}
                        </div>
                      )}
                      <button className="faq-upload-remove" onClick={() => removePendingFile(pf.id)}>
                        <CloseCircleFilled />
                      </button>
                      {pf.progress > 0 && pf.progress < 100 && (
                        <div className="faq-upload-progress">
                          <div className="faq-upload-progress-bar" style={{ width: `${pf.progress}%` }} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Input Area */}
              <div className="faq-chat-input-area">
                <div className="faq-chat-input-row">
                  <div className="faq-chat-input-actions">
                    <button onClick={() => fileInputRef.current?.click()} title="Attach file">
                      <PaperClipOutlined />
                    </button>
                  </div>
                  <div className="faq-chat-input-field">
                    <textarea
                      ref={textareaRef}
                      rows={1}
                      placeholder={
                        mode === "quote"
                          ? "Tell us where you're moving from and to..."
                          : "Type your message..."
                      }
                      value={input}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                      disabled={sending || uploading}
                    />
                  </div>
                  <button
                    className="faq-chat-send-btn"
                    onClick={handleSend}
                    disabled={sending || uploading || (!input.trim() && pendingFiles.length === 0)}
                    title="Send"
                  >
                    {sending || uploading ? <Spinner size="small" /> : <SendOutlined />}
                  </button>
                </div>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ALLOWED_TYPES.join(",")}
                style={{ display: "none" }}
                onChange={handleFileSelect}
              />
            </>
          )}

          {/* Footer */}
          <div className="faq-chat-footer">
            Powered by <a href="https://qiktruck.co.za" target="_blank" rel="noopener noreferrer">QikTruck AI</a>
          </div>
        </div>
      )}

      {/* ── FAB ────────────────────────────────────────────────── */}
      {!open && fabPulse && (
        <div className="faq-chat-fab-label">Need help? Chat with us!</div>
      )}
      <button
        className={`faq-chat-fab ${open ? "faq-chat-fab--open" : ""}`}
        onClick={() => setOpen(!open)}
        aria-label={open ? "Close chat" : "Open chat"}
      >
        {open ? <CloseOutlined /> : <MessageOutlined />}
        {!open && unread > 0 && (
          <span className="faq-chat-fab-badge">{unread > 9 ? "9+" : unread}</span>
        )}
      </button>
    </div>
  );
};

export default FaqAgentChat;
